import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import XIcon from "@mui/icons-material/X";
import {
  Avatar,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { EvotruxDivider } from "components/EvotruxDivider";

import type { ReactNode } from "react";

import type { CompanyProfileAbout } from "types/company";

interface Props {
  companyName: string;
  profileAbout: CompanyProfileAbout | null;
}

interface SocialLink {
  platform: string;
  url: string | undefined;
  icon: ReactNode;
}

export function CompanyProfileFooter(props: Props) {
  const { companyName, profileAbout } = props;

  const socialLinks: SocialLink[] = [
    {
      platform: "Facebook",
      url: profileAbout?.facebook_url,
      icon: <FacebookIcon />,
    },
    { platform: "X", url: profileAbout?.x_url, icon: <XIcon /> },
    {
      platform: "LinkedIn",
      url: profileAbout?.linkedin_url,
      icon: <LinkedInIcon />,
    },
    {
      platform: "Instagram",
      url: profileAbout?.instagram_url,
      icon: <InstagramIcon />,
    },
  ];

  return (
    <Stack spacing={4} sx={{ mt: 4 }}>
      <EvotruxDivider />
      <Stack alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocalShippingOutlinedIcon color="primary" fontSize="medium" />
          <Typography variant="h6">{companyName}</Typography>
        </Stack>
        {profileAbout?.website_url && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <LanguageIcon />
            <Tooltip title={`Open the ${companyName} website in a new tab`}>
              <Link
                color="inherit"
                href={profileAbout.website_url}
                underline="hover"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {profileAbout.website_url}
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Tooltip>
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          {socialLinks.map(
            (socialLink) =>
              socialLink.url && (
                <Tooltip
                  key={socialLink.platform}
                  title={`Open ${companyName} on ${socialLink.platform} in a new tab`}
                >
                  <IconButton
                    aria-label={`Open ${companyName} on ${socialLink.platform} in a new tab`}
                    component="a"
                    href={socialLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Avatar>{socialLink.icon}</Avatar>
                  </IconButton>
                </Tooltip>
              ),
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
